import { useEffect, useRef, useState } from "react";

type LoadingPhase = "idle" | "suppressed" | "spinner" | "holding";

type DeferredLoadingOptions = {
  delay?: number;
  minVisible?: number;
};

type DeferredLoadingResult = {
  showIndicator: boolean;
  suppress: boolean;
};

export const DEFERRED_LOADING_DEFAULTS = {
  delay: 500,
  minVisible: 1000,
} as const;

/**
 * Defers showing a loading indicator to avoid brief flashes.
 *
 * @param isLoading - Whether data is currently being fetched.
 * @param isActive - Whether the hook should run. When false the state machine
 *   resets to idle and no timers fire. Use this to gate on preconditions like
 *   the modal being open or sync blockers being present.
 *
 * - During the first `delay` ms of loading, suppresses all UI (suppress=true).
 * - If loading finishes within `delay`, the indicator is never shown.
 * - If loading exceeds `delay`, shows the indicator (showIndicator=true).
 * - Once the indicator is shown, it stays visible for at least `minVisible` ms.
 */
export const useDeferredLoading = (
  isLoading: boolean,
  isActive: boolean,
  {
    delay = DEFERRED_LOADING_DEFAULTS.delay,
    minVisible = DEFERRED_LOADING_DEFAULTS.minVisible,
  }: DeferredLoadingOptions = {},
): DeferredLoadingResult => {
  const [phase, setPhase] = useState<LoadingPhase>("idle");
  const spinnerVisibleSince = useRef(0);

  // Reset on deactivation
  useEffect(() => {
    if (!isActive) {
      setPhase("idle");
      spinnerVisibleSince.current = 0;
    }
  }, [isActive]);

  // idle → suppressed (loading starts)
  useEffect(() => {
    if (isActive && isLoading && phase === "idle") {
      setPhase("suppressed");
    }
  }, [isActive, isLoading, phase]);

  // suppressed → spinner (delay elapsed, still loading)
  useEffect(() => {
    if (phase !== "suppressed" || !isLoading) return;
    const timer = setTimeout(() => {
      spinnerVisibleSince.current = Date.now();
      setPhase("spinner");
    }, delay);
    return () => clearTimeout(timer);
  }, [phase, isLoading, delay]);

  // suppressed → idle (fast load)
  useEffect(() => {
    if (!isLoading && phase === "suppressed") {
      setPhase("idle");
    }
  }, [isLoading, phase]);

  // spinner → holding or idle (loading finished while spinner visible)
  useEffect(() => {
    if (!isLoading && phase === "spinner") {
      const elapsed = Date.now() - spinnerVisibleSince.current;
      if (elapsed < minVisible) {
        setPhase("holding");
      } else {
        setPhase("idle");
        spinnerVisibleSince.current = 0;
      }
    }
  }, [isLoading, phase, minVisible]);

  // holding → idle (remaining min visible time elapsed)
  useEffect(() => {
    if (phase !== "holding") return;
    const elapsed = Date.now() - spinnerVisibleSince.current;
    const remaining = Math.max(0, minVisible - elapsed);
    const timer = setTimeout(() => {
      setPhase("idle");
      spinnerVisibleSince.current = 0;
    }, remaining);
    return () => clearTimeout(timer);
  }, [phase, minVisible]);

  return {
    suppress:
      isActive && isLoading && (phase === "idle" || phase === "suppressed"),
    showIndicator: phase === "spinner" || phase === "holding",
  };
};
